// TODO: Body type needs doing
import { ADT } from "pb.adt";
import {
  authenticatedFetch,
  getFlowUri,
  QueryFromVars,
  throwBody,
  throwHttpError,
  type VarToQuery,
} from "@/api/shared.ts";
import type { SocratesSession } from "@/types/socrates.ts";
import { JsonParse } from "@/helpers/json.ts";
import { type HeldReplyState, HeldReplyStates } from "@/stores/useChatStore.ts";
import type { HeldReplyStatus } from "@/types/signalr-functions.ts";

const JsonHeaders = { "Content-Type": "application/json" };

/**
 * Pre-generation hold: the STUDENT's own message was flagged by moderation
 * before the AI answered. The student sees only neutral holding copy while a
 * tutor reviews; metadata is a JSON string (heldForReview + atoms judgement).
 */
export interface HeldStudentMessage {
  flowGeneratorSessionInterventionMessageId: number;
  message: string;
  metadata: string;
  dateCreated: string;
}

export interface FlaggedDimension {
  dimension: string;
  reason: string;
}

export interface HeldReplyAtoms {
  scope: string;
  verdict: string;
  errorCode: string;
  degraded: boolean;
  errorType: string | null;
  flaggedDimensions: Array<FlaggedDimension>;
  judgedAt: string;
}

export interface HeldReplyReview {
  outcome: "approved" | "approvedWithEdits" | "claimed";
  byTutorUserId: number;
  date: string;
  // Present only on an edited approve
  originalMessage?: string;
}

/**
 * Parsed shape of ChatResponse.metadata (a JSON string) for safety-held replies.
 * Written by the flow API's safety judge; "needs review" = heldForReview true
 * AND no review property.
 */
export interface HeldReplyMetadata {
  socratesInteractionId?: string;
  heldForReview?: boolean;
  atoms?: HeldReplyAtoms;
  review?: HeldReplyReview;
}

/**
 * One held item awaiting review — either the AI's withheld draft (post-eval
 * hold) or the student's flagged message (pre-eval hold).
 */
export interface HeldItem {
  // Identity of the held row — a new value means a NEW item replaced the old
  // one (superseded), which is the signal to reset any review UI
  messageId?: number;
  message?: string;
  atoms?: HeldReplyAtoms;
  dateCreated?: string;
}

export const IsHeldReplyStateSomething = (state: HeldReplyState | undefined) =>
  ADT.match(state ?? HeldReplyStates.unknown(), ["resolved", "held"]);

export const IsHeldReplyStateHeld = (state: HeldReplyState | undefined) =>
  ADT.match(state ?? HeldReplyStates.unknown(), ["held"]);

/** Shared identity scheme for held/resolved states — see HeldReplyState. */
export const holdIdentity = (student?: HeldItem, reply?: HeldItem): string =>
  [
    student ? `s:${student.messageId ?? student.message}` : null,
    reply ? `r:${reply.messageId ?? reply.message}` : null,
  ]
    .filter(Boolean)
    .join("|");

export const deriveHeldReplyState = (response: HeldReplyStatus | null): HeldReplyState => {
  if (!response) return HeldReplyStates.none();

  // Pre-eval hold: the row's presence IS the hold (its metadata enriches the
  // display with the atoms judgement). Defensive: a review-stamped row is
  // already dealt with, mirroring the reply-hold rule below.
  const heldStudent = response.heldStudentMessage;
  const studentMetadata = heldStudent?.metadata
    ? JsonParse<HeldReplyMetadata>(heldStudent.metadata)
    : null;
  const student: HeldItem | undefined =
    heldStudent && !studentMetadata?.review
      ? {
          messageId: heldStudent.flowGeneratorSessionInterventionMessageId,
          message: heldStudent.message,
          atoms: studentMetadata?.atoms,
          dateCreated: heldStudent.dateCreated,
        }
      : undefined;

  const metadata = response.metadata ? JsonParse<HeldReplyMetadata>(response.metadata) : null;

  // Post-eval hold: the top-level draft, held and not yet review-stamped (a
  // review stamp wins — the draft was already dealt with)
  const reply: HeldItem | undefined =
    response.message && metadata?.heldForReview === true && !metadata.review
      ? {
          messageId: response.flowGeneratorSessionInterventionMessageId,
          message: response.message,
          atoms: metadata.atoms,
        }
      : undefined;

  // A student hold coexists with (and outlives) a resolved reply stamp — any
  // unresolved hold makes the whole session "held"
  if (student || reply)
    return HeldReplyStates.held({ identity: holdIdentity(student, reply), student, reply });

  if (response.message && metadata?.review)
    return HeldReplyStates.resolved({
      review: metadata.review,
      identity: holdIdentity(undefined, {
        messageId: response.flowGeneratorSessionInterventionMessageId,
        message: response.message,
      }),
      message: response.message,
    });

  if (!response.message) return HeldReplyStates.none();

  // A pending message without hold markers: either a plain (unheld) AI draft
  // or clobbered metadata — indistinguishable, so unknown.
  return HeldReplyStates.unknown();
};

export const getChatResponse = async (flowSessionId: number): Promise<HeldReplyStatus | null> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/response`),
    {
      method: "GET",
      headers: JsonHeaders,
      priority: "low",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const getPendingResponses = async (): Promise<null> => {
  const response = await authenticatedFetch(getFlowUri("/v1/chat/ai-tutor/pending-responses"), {
    method: "GET",
    headers: JsonHeaders,
    priority: "low",
  });

  if (!response.ok) throw await throwBody(response);

  console.log(await response.json());

  return null;
};

export interface ApproveResponseBody {
  // Reviewer note kept for the audit trail only — never shown to the AI or
  // the student
  feedback?: string;
  // Held AI replies only: omit (or send unchanged) for a plain approve; a
  // normalized difference from the held text makes it an approve-with-edits
  // server-side. NEVER send for a held student message — the server 400s
  // ("A held student message cannot be edited").
  editedMessage?: string;
  // Held STUDENT messages only: the reviewer's REQUIRED verdict on the flag.
  // true = the safety judge was wrong (harmless), false = the flag was right
  // but the session may continue. Either way the AI resumes — this only
  // records the judgement for the safety team. Leave unset for held AI
  // replies (no classification there).
  markedAsFalsePositive?: boolean;
}

/**
 * Approves a session's hold as a false positive.
 *
 * Held AI reply: 204 = delivered to the student as the AI tutor — the session
 * STAYS queued and the AI STAYS paused.
 * Held student message: 204 = AI resumed and the student's message re-submitted
 * (the AI will answer it) — the session LEAVES the queue.
 *
 * Errors are thrown as HttpError so callers can branch:
 * 404 no open intervention (drop the item), 409 a human now owns the session
 * or claimed mid-approve (refresh; note the throttle filter also 409s — see
 * isThrottled409), 410 the hold was superseded or already resolved (refresh).
 */
export const postChatResponseApprove = async (
  flowSessionId: number,
  body: ApproveResponseBody,
): Promise<void> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/response/approve`),
    {
      method: "POST",
      headers: JsonHeaders,
      body: JSON.stringify(body),
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwHttpError(response);
  }
};

export const getChatRegenerateResponse = async (flowSessionId: number): Promise<unknown> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/regenerate-response`),
    {
      method: "GET",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

interface GenerateTutorResponse {
  Prompt: string;
}

export const postChatRegenerateResponse = async (
  flowSessionId: number,
  responseMint: string | null,
  studentMessage: string | null,
): Promise<GenerateTutorResponse> => {
  const query = QueryFromVars([
    { name: "responseMint", value: responseMint },
    { name: "studentMessage", value: studentMessage },
  ]);

  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/regenerate-response${query}`),
    {
      method: "POST",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const postChatCancelResponse = async (flowSessionId: number) => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/cancel-response`),
    {
      method: "POST",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }
};

interface PedagogicalPlan {
  PedagogicalPlanId: string;
  PlanStatus: string;
  LearnerSessionId: string;
  Status: string;
}

export const getChatPedagogicalPlan = async (
  flowSessionId: number,
): Promise<PedagogicalPlan | null> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/pedagogical-plan`),
    {
      method: "GET",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const getChatSocratesSession = async (
  flowSessionId: number,
  includes: Array<VarToQuery>,
): Promise<SocratesSession | null> => {
  const response = await authenticatedFetch(
    getFlowUri(
      `/v1/chat/flow-generator-sessions/${flowSessionId}/socrates-session${QueryFromVars(includes)}`,
    ),
    {
      method: "GET",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

interface ModeratePlan {
  IsApproved: boolean;
  ModerationFeedback?: string;
  ModeratedByExternalId?: string;
  ModeratedByExternalIdProvider?: string;
}

export const postChatModeratePlan = async (
  flowSessionId: number,
  modPlan: ModeratePlan,
): Promise<ModeratePlan> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/chat/flow-generator-sessions/${flowSessionId}/pedagogical-plan/moderate`),
    {
      method: "POST",
      headers: JsonHeaders,
      body: JSON.stringify(modPlan),
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};
